import React from "react";
import { Search, X, Filter } from "lucide-react";

/**
 * Reusable filter bar for orders/users/shops.
 * `fields` is array of { key, type: 'text'|'select'|'date', label, options? }
 * `values` is the current value object.
 * `onChange(values)` is the controlled callback.
 */
export default function FilterBar({ fields, values, onChange, onClear, className = "" }) {
  const set = (k, v) => onChange({ ...values, [k]: v });
  const hasFilters = Object.values(values || {}).some(v => v !== "" && v !== null && v !== undefined);

  return (
    <div className={`bg-white rounded-2xl soft-shadow p-3 sm:p-4 ${className}`} data-testid="filter-bar">
      <div className="flex flex-wrap items-center gap-2">
        <div className="flex items-center gap-1.5 text-[#86868B] text-xs uppercase tracking-wider font-bold mr-1">
          <Filter className="w-3.5 h-3.5" /> Filters
        </div>
        {fields.map(f => {
          if (f.type === "text") return (
            <div key={f.key} className="relative flex-1 min-w-[160px]">
              <Search className="w-3.5 h-3.5 text-[#86868B] absolute left-3 top-1/2 -translate-y-1/2" />
              <input
                placeholder={f.label}
                value={values[f.key] || ""}
                onChange={e => set(f.key, e.target.value)}
                data-testid={`filter-${f.key}`}
                className="w-full bg-[#F5F5F7] rounded-lg pl-8 pr-3 py-2 text-sm outline-none focus:ring-2 focus:ring-[#0055FF]/30"
              />
            </div>
          );
          if (f.type === "select") return (
            <select
              key={f.key}
              value={values[f.key] || ""}
              onChange={e => set(f.key, e.target.value)}
              data-testid={`filter-${f.key}`}
              className="bg-[#F5F5F7] rounded-lg px-3 py-2 text-sm outline-none focus:ring-2 focus:ring-[#0055FF]/30 min-w-[120px]"
            >
              <option value="">{f.label}</option>
              {f.options.map(o => <option key={o.value} value={o.value}>{o.label}</option>)}
            </select>
          );
          if (f.type === "date") return (
            <div key={f.key} className="flex items-center gap-1">
              <span className="text-xs text-[#86868B]">{f.label}</span>
              <input
                type="date"
                value={values[f.key] || ""}
                onChange={e => set(f.key, e.target.value)}
                data-testid={`filter-${f.key}`}
                className="bg-[#F5F5F7] rounded-lg px-2 py-2 text-sm outline-none focus:ring-2 focus:ring-[#0055FF]/30"
              />
            </div>
          );
          return null;
        })}
        {hasFilters && (
          <button
            onClick={onClear}
            data-testid="filter-clear"
            className="bg-rose-50 hover:bg-rose-100 text-[#FF3B30] rounded-lg px-3 py-2 text-sm font-bold flex items-center gap-1"
          >
            <X className="w-3.5 h-3.5" /> Clear
          </button>
        )}
      </div>
    </div>
  );
}
